import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraEntityCell } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { useCapabilitySummaries } from '../../Registries/list/useRegistrySummaries';
import { useShowMore } from '../../Registries/list/useShowMore';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { capabilityHooks } from './hooks';
import { RecordUnavailable } from './RecordUnavailable';
import { useRegistryRecord } from './registryParams';
const STATUS_OPTIONS = [
    {
        value: 'active',
        label: 'Active'
    },
    {
        value: 'retired',
        label: 'Retired'
    },
    {
        value: 'all',
        label: 'All'
    }
];
// Category suggestions come from the categories already in use; free text stays allowed.
const categoryField = (suggestions)=>({
        kind: 'text',
        name: 'category',
        label: 'Category',
        maxLength: 50,
        suggestions,
        testId: Testids.deliveryRegistries.capabilityCategory
    });
const capabilityFields = (categories)=>[
        {
            kind: 'text',
            name: 'name',
            label: 'Name',
            required: true,
            maxLength: 50
        },
        {
            kind: 'textarea',
            name: 'description',
            label: 'Description',
            rows: 3
        },
        categoryField(categories)
    ];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const capabilityDefaults = (c)=>({
        id: c?.id ?? '',
        name: c?.name ?? '',
        description: c?.description ?? '',
        category: c?.category ?? ''
    });
// Read sections plus, per row, the inline-edit control. Name/Description reuse
// the SAME field configs the create form defines; Source is derived and stays
// read-only.
function capabilityDetailSections(c, categories) {
    return [
        {
            heading: 'Capability',
            rows: [
                {
                    label: 'Name',
                    value: c.name || '—',
                    editField: {
                        kind: 'text',
                        name: 'name',
                        label: 'Name',
                        required: true,
                        maxLength: 50
                    }
                },
                {
                    label: 'Description',
                    value: c.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        rows: 3
                    }
                },
                {
                    label: 'Category',
                    value: c.category || '—',
                    // System starters are not tenant-editable, so only tenant rows edit it.
                    editField: categoryField(categories)
                },
                // Derived, not part of the create form — read-only in edit mode too.
                {
                    label: 'Source',
                    value: c.isSystem ? 'System default' : 'Custom'
                }
            ]
        }
    ];
}
export function CapabilitiesTab() {
    const tenantId = useTenantId();
    const recordsEnabled = useRegistryRecordsEnabled();
    // SAD-948: the shared registries are edited only by area:registries holders.
    const canEdit = useTenantAreas().registries;
    // Ask for retired rows too; the status filter below narrows client-side.
    const { data, isLoading } = capabilityHooks.useList({
        companyId: tenantId,
        includeRetired: true
    });
    const upsert = capabilityHooks.useUpsert();
    const retire = capabilityHooks.useRetire();
    const reactivate = capabilityHooks.useReactivate();
    const [status, setStatus] = useState('active');
    const allItems = data?.items ?? [];
    const categories = [
        ...new Set(allItems.map((c)=>c.category).filter(Boolean))
    ].sort();
    const items = allItems.filter((c)=>status === 'all' ? true : status === 'retired' ? c.isRetired : !c.isRetired);
    const window = useShowMore(items, status);
    const visible = recordsEnabled ? window.visible : items;
    const summaries = useCapabilitySummaries(recordsEnabled ? visible.map((c)=>c.id) : []);
    const summaryById = new Map((summaries.isError ? [] : summaries.data ?? []).map((s)=>[
            s.capabilityId,
            s
        ]));
    const [formOpen, setFormOpen] = useState(false);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list without an effect that re-syncs
    // state (which infinite-loops on fresh-but-equal references and hangs).
    // The id lives in the URL (?record=); one that resolves to nothing shows the
    // generic unavailable state. Resolved against the unfiltered list so a
    // retired record still opens under the default "active" filter.
    const { recordId: detailId, openRecord, closeRecord } = useRegistryRecord();
    const detail = detailId ? allItems.find((c)=>c.id === detailId) ?? null : null;
    const recordUnavailable = !!detailId && !isLoading && !detail;
    const [detailMode, setDetailMode] = useState('read');
    const [retiring, setRetiring] = useState(null);
    const columns = [
        {
            key: 'name',
            header: 'Name'
        },
        {
            key: 'description',
            header: 'Description'
        },
        {
            key: 'source',
            header: 'Source',
            render: (row)=>row.isSystem ? 'System default' : 'Custom'
        },
        {
            key: 'status',
            header: 'Status',
            render: (row)=>row.isRetired ? /*#__PURE__*/ _jsx("span", {
                    className: "text-xs rounded px-1.5 py-0.5 bg-muted text-muted-foreground",
                    "data-testid": Testids.deliveryRegistries.capabilityRetiredBadge,
                    children: "Retired"
                }) : 'Active'
        }
    ];
    const recordColumns = [
        {
            key: 'name',
            header: 'Capability',
            render: (c)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: c.name,
                    description: c.category || (c.isSystem ? 'System default' : 'Custom')
                })
        },
        columns[3],
        {
            key: 'proof',
            header: 'Proof',
            render: ()=>'—'
        },
        {
            key: 'contracts',
            header: 'Contracts',
            render: (c)=>{
                const summary = summaryById.get(c.id);
                if (!summary?.visiblePrograms.length) return '—';
                const extra = Math.max(0, summary.visibleProgramCount - summary.visiblePrograms.length);
                return summary.visiblePrograms.map((p)=>p.name).join(', ') + (extra ? ` +${extra}` : '');
            }
        },
        {
            key: 'asked',
            header: 'Asked for in',
            render: ()=>'—'
        },
        {
            key: 'gaps',
            header: 'Gaps',
            render: ()=>'—'
        }
    ];
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create).
    const submitCapability = async (values, id, base)=>{
        const category = (values.category ?? '').trim();
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            name: values.name ?? '',
            description: values.description ?? '',
            // Sent only when changed: omitted leaves the stored category alone.
            ...category !== (base?.category ?? '') && {
                category
            }
        });
    };
    const handleRetire = async ()=>{
        if (!retiring) return;
        try {
            await retire.mutateAsync({
                id: retiring.id,
                companyId: tenantId
            });
        } catch  {
        // The hook's onError already toasted the server message.
        }
        setRetiring(null);
    };
    // The 6 seeded defaults are read-only (the server refuses writes to _system
    // rows), so the drawer offers edit only on tenant-added rows.
    const canEditDetail = canEdit && !!detail && !detail.isSystem;
    const detailEdit = detail && !detail.isSystem ? {
        defaultValues: capabilityDefaults(detail),
        onSubmit: (v)=>submitCapability(v, detail.id, detail),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs(Select, {
                        value: status,
                        onValueChange: (v)=>setStatus(v),
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "w-40",
                                "aria-label": "Status",
                                "data-testid": Testids.deliveryRegistries.capabilityStatusFilter,
                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: STATUS_OPTIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: o.value,
                                        children: o.label
                                    }, o.value))
                            })
                        ]
                    }),
                    canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setFormOpen(true),
                        "data-testid": Testids.deliveryRegistries.capabilityCreate,
                        children: "New Capability"
                    })
                ]
            }),
            recordUnavailable && /*#__PURE__*/ _jsx(RecordUnavailable, {
                testId: Testids.deliveryRegistries.recordUnavailable,
                onDismiss: closeRecord
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: visible,
                columns: recordsEnabled ? recordColumns : columns,
                testid: Testids.deliveryRegistries.capabilitiesTable,
                rowTestid: Testids.deliveryRegistries.capabilityRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    openRecord(row.id);
                },
                hideRowActions: !canEdit,
                rowActions: (row)=>/*#__PURE__*/ _jsx("div", {
                        className: "flex gap-2 justify-end",
                        children: row.isRetired ? /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "ghost",
                            disabled: reactivate.isPending,
                            onClick: ()=>reactivate.mutate({
                                    id: row.id,
                                    companyId: tenantId
                                }),
                            "data-testid": Testids.deliveryRegistries.capabilityReactivate,
                            children: "Reactivate"
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                row.isSystem ? /*#__PURE__*/ _jsx("span", {
                                    className: "text-xs text-muted-foreground self-center",
                                    children: "System default"
                                }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    // Edit-in-place: open the drawer straight into edit mode.
                                    onClick: ()=>{
                                        setDetailMode('edit');
                                        openRecord(row.id);
                                    },
                                    "data-testid": Testids.deliveryRegistries.capabilityEdit,
                                    children: "Edit"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    className: "text-danger hover:bg-danger-muted hover:text-danger",
                                    onClick: ()=>setRetiring(row),
                                    "data-testid": Testids.deliveryRegistries.capabilityRetire,
                                    children: "Retire"
                                })
                            ]
                        })
                    })
            }),
            recordsEnabled && window.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": Testids.deliveryRegistries.showMore,
                onClick: window.showMore,
                children: "Show more"
            }),
            recordsEnabled && /*#__PURE__*/ _jsx(RegistryListNote, {
                children: "What we can do, backed by contracts that prove it. Feeds RFI answers and past-performance write-ups."
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Capability",
                fields: capabilityFields(categories),
                defaultValues: capabilityDefaults(null),
                onSubmit: async (v)=>{
                    await submitCapability(v, '', null);
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && closeRecord(),
                title: detail ? detail.name || 'Capability' : '',
                sections: detail ? capabilityDetailSections(detail, categories) : [],
                testid: Testids.deliveryRegistries.capabilityDetailDrawer,
                edit: detailEdit,
                canEdit: canEditDetail,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!retiring,
                onOpenChange: (open)=>!open && setRetiring(null),
                title: "Retire Capability",
                description: retiring ? `Retire "${retiring.name}"? It will no longer be offered for new selections; programs that already use it keep its name.` : undefined,
                onConfirm: handleRetire,
                isPending: retire.isPending,
                confirmLabel: "Retire",
                pendingLabel: "Retiring…"
            })
        ]
    });
}
